import * as React from 'react';
import { cn } from '@/lib/utils';

export interface ToggleProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  /** Controlled checked state (on/off). */
  checked: boolean;
  /** Called when user toggles; pass new boolean. */
  onCheckedChange: (checked: boolean) => void;
  /** Optional label above the toggle (e.g. "Status"). */
  label?: string;
  /** Id for the toggle button (for label association). */
  id?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Custom toggle (switch) for boolean or status-like values.
 * Use with react-hook-form Controller for status: checked = (value === 'active'), onCheckedChange = (c) => setValue('status', c ? 'active' : 'inactive').
 */
export const Toggle = React.forwardRef<HTMLButtonElement, ToggleProps>(
  (
    {
      checked,
      onCheckedChange,
      label,
      id,
      disabled = false,
      className,
      ...props
    },
    ref
  ) => {
    const handleClick = () => {
      if (!disabled) onCheckedChange(!checked);
    };

    return (
      <div className={cn('space-y-1', className)}>
        {label && (
          <label htmlFor={id} className="block text-sm font-medium text-foreground">
            {label}
          </label>
        )}
        <button
          ref={ref}
          type="button"
          role="switch"
          aria-checked={checked}
          id={id}
          disabled={disabled}
          onClick={handleClick}
          className={cn(
            'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            checked
              ? 'border-primary bg-primary'
              : 'border-border bg-muted'
          )}
          {...props}
        >
          <span
            className={cn(
              'pointer-events-none inline-block h-5 w-5 rounded-full bg-background border border-border shadow-sm transition-transform',
              checked ? 'translate-x-6' : 'translate-x-0.5'
            )}
          />
        </button>
      </div>
    );
  }
);
Toggle.displayName = 'Toggle';
